import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { fetcher } from '@dashboard/services/request';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { toast } from '@dashboard/context/ToastContext';
import { blockedCustomersKey } from '@dashboard/lib/queryKeys';

const normalizeEmail = email => (email ?? '').trim().toLowerCase();

/**
 * One fetch of the store's blocklist answers every badge on the page, which is
 * what the customers list needs: a per-email lookup would be one request per
 * row. It shares a query key with the settings table, so the two never disagree
 * and a page that has already loaded one gets the other free.
 *
 * The id is carried alongside because unblocking deletes that row.
 */
export function useBlockedEmails() {
  const { sellerId, token } = useAuth();

  const { data } = useQuery({
    queryKey: blockedCustomersKey(sellerId),
    queryFn: () => fetcher(`/sellers/${sellerId}/blocked-customers`, token),
    enabled: !!sellerId,
  });

  const blockedIdByEmail = useMemo(() => {
    const entries = data?.blockedCustomers ?? [];
    return new Map(entries.map(entry => [normalizeEmail(entry.email), entry.id]));
  }, [data]);

  return {
    isBlocked: email => blockedIdByEmail.has(normalizeEmail(email)),
    blockedIdFor: email => blockedIdByEmail.get(normalizeEmail(email)) ?? null,
  };
}

function useInvalidateBlocked() {
  const { sellerId } = useAuth();
  const queryClient = useQueryClient();

  return () => queryClient.invalidateQueries({ queryKey: blockedCustomersKey(sellerId) });
}

/**
 * Shared by the settings dialog and the block action on customer detail, so the
 * toasts, the already-blocked case and the cache invalidation stay identical
 * wherever a merchant blocks an address.
 */
export function useBlockEmail({ onSuccess } = {}) {
  const { sellerId, token } = useAuth();
  const invalidate = useInvalidateBlocked();

  return useMutation({
    mutationFn: values =>
      fetcher(`/sellers/${sellerId}/blocked-customers`, token, {
        method: 'POST',
        body: JSON.stringify(values),
      }),
    onSuccess: data => {
      invalidate();
      toast({
        title: data?.alreadyBlocked ? 'That email is already blocked' : 'Email blocked',
        type: 'success',
      });
      onSuccess?.(data);
    },
    onError: error => {
      toast({
        title: 'Could not block that email',
        description: error?.message || 'Check the address and try again',
        type: 'error',
      });
    },
  });
}

export function useUnblockEmail({ onSuccess } = {}) {
  const { sellerId, token } = useAuth();
  const invalidate = useInvalidateBlocked();

  return useMutation({
    mutationFn: blockedCustomerId =>
      fetcher(`/sellers/${sellerId}/blocked-customers/${blockedCustomerId}`, token, {
        method: 'DELETE',
      }),
    onSuccess: data => {
      invalidate();
      toast({ title: 'Email unblocked', type: 'success' });
      onSuccess?.(data);
    },
    onError: error => {
      toast({
        title: 'Could not unblock that email',
        description: error?.message || 'Please try again',
        type: 'error',
      });
    },
  });
}

export function useUpdateBlockedEmailNote({ onSuccess } = {}) {
  const { sellerId, token } = useAuth();
  const invalidate = useInvalidateBlocked();

  return useMutation({
    mutationFn: ({ blockedCustomerId, note }) =>
      fetcher(`/sellers/${sellerId}/blocked-customers/${blockedCustomerId}`, token, {
        method: 'PATCH',
        body: JSON.stringify({ note }),
      }),
    onSuccess: data => {
      invalidate();
      toast({ title: 'Reason updated', type: 'success' });
      onSuccess?.(data);
    },
    onError: error => {
      toast({
        title: 'Could not update that reason',
        description: error?.message || 'Please try again',
        type: 'error',
      });
    },
  });
}
